import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { castArray, isEqual } from 'lodash-es';
import { ExecDashboardRequestBody } from '../../shared/models/exec-dashboard-request-body.model';
import { StormDashboardRequestBody } from '../../shared/models/storm-dashboard-request-body.model';
import { Pagination } from '../../shared/models';
import { LocalStorageService } from '../../xform-compat';
import { UserFocusComponent } from '../../shared/components/user-focus/user-focus.component';

/** Types used for query param parsing */
export type ParamType = 'numericArray' | 'stringArray' | 'boolean' | 'number' | 'string';

/** Return type for parseQueryParam based on ParamType */
type ParsedQueryParam = string | number | boolean | Array<string> | Array<number> | null;

/**
 * Service to handle shared filter state and utility methods across the exec dashboard components.
 * Provides centralized query param serialization, localStorage persistence, and user focus state management.
 */
@Injectable()
export class ExecDashboardUtilsService {
  get execDashboardRequestBody(): ExecDashboardRequestBody {
    return this._execDashboardRequestBody;
  }
  set execDashboardRequestBody(value: ExecDashboardRequestBody) {
    this._execDashboardRequestBody = value;
  }

  get stormDashboardRequestBody(): StormDashboardRequestBody {
    return this._stormDashboardRequestBody;
  }
  set stormDashboardRequestBody(value: StormDashboardRequestBody) {
    this._stormDashboardRequestBody = value;
  }

  private _execDashboardRequestBody: ExecDashboardRequestBody;
  private _stormDashboardRequestBody: StormDashboardRequestBody;
  public filterChanged$ = new Subject<{value: any, filter: string, flag?: boolean, fieldName?: string}>();
  public privateFilterChanged$ = new Subject<{value: any, filter: string, flag?: boolean, fieldName?: string}>();

  /** Hydration signals — table components emit after loading state from localStorage/query params.
   *  Filter components subscribe to wait for hydration before reading shared body state. */
  private _execBodyHydrated$ = new ReplaySubject<void>(1);
  private _stormBodyHydrated$ = new ReplaySubject<void>(1);
  public execBodyHydrated$: Observable<void> = this._execBodyHydrated$.asObservable();
  public stormBodyHydrated$: Observable<void> = this._stormBodyHydrated$.asObservable();

  constructor(private localStorageService: LocalStorageService) {}

  /**
   * Market cap / enterprise value range filters are entered and displayed in millions, but the
   * backend expects raw values. Scales the relevant fields on the given body in place.
   */
  public static scaleMillionRangeFields(body: Record<string, any>): void {
    for (const key of ['marketCapLowRange', 'marketCapHighRange', 'evLowRange', 'evHighRange']) {
      if (body[key]) {
        body[key] = Number(body[key]) * 1000000;
      }
    }
  }

  public static convertToBoolean(value: unknown): boolean {
    if (value === undefined || value === null) {
      return false;
    } else if (typeof value === 'boolean') {
      return value;
    } else if (typeof value === 'string') {
      return String(value).toLowerCase() === 'true';
    }
    return false;
  }

  /**
   * Parses a single query param value into the correct type.
   * Empty string is used as a sentinel for "explicitly empty array" since Angular Router
   * strips actual empty arrays from query params.
   * @param value Raw value from the URL (always a string or string[])
   * @param type The target type to coerce to
   */
  public static parseQueryParam(value: unknown, type: ParamType): ParsedQueryParam {
    switch (type) {
      case 'numericArray':
        return value === '' ? [] : castArray(value).map(d => +d);
      case 'stringArray':
        return value === '' ? [] : castArray(value) as Array<string>;
      case 'boolean':
        return ExecDashboardUtilsService.convertToBoolean(value);
      case 'number': {
        if (value == null) {
          return null;
        }
        const n = +value;
        return isNaN(n) ? null : n;
      }
      case 'string':
        return value as string;
    }
  }

  /**
   * Builds query params containing only values that differ from the defaults.
   * Only includes keys present in allowedKeys to prevent internal fields (active, isDeleted, etc.)
   * from leaking into the URL.
   */
  public static buildNonDefaultQueryParams(
    current: Record<string, any>,
    defaults: Record<string, any>,
    pagination: Pagination,
    allowedKeys?: Set<string>,
  ): Record<string, any> {
    const queryParams: Record<string, any> = {};
    for (const [key, value] of Object.entries(current)) {
      if (allowedKeys && !allowedKeys.has(key)) {
        continue;
      }
      if (!isEqual(value, defaults[key])) {
        // Angular Router strips empty arrays from query params, so use empty string
        // as a sentinel to represent "explicitly empty" (distinguishable from "not set").
        queryParams[key] = (Array.isArray(value) && value.length === 0) ? '' : value;
      }
    }
    if (pagination.page > 1) {
      queryParams['page'] = pagination.page;
    }
    if (defaults['pagesize'] !== undefined && pagination.pagesize !== defaults['pagesize']) {
      queryParams['pagesize'] = pagination.pagesize;
    }
    return queryParams;
  }

  /**
   * Updates URL query params using only non-default values, with replaceUrl to avoid polluting browser history.
   * Remains static because Router and ActivatedRoute are route-scoped (different per component).
   */
  public static updateQueryParams(
    router: Router,
    activatedRoute: ActivatedRoute,
    current: Record<string, any>,
    defaults: Record<string, any>,
    pagination: Pagination,
    allowedKeys?: Set<string>,
  ): void {
    const queryParams = ExecDashboardUtilsService.buildNonDefaultQueryParams(current, defaults, pagination, allowedKeys);
    router.navigate([], { relativeTo: activatedRoute, queryParams, replaceUrl: true });
  }

  /** Called by table components after hydrating shared body from localStorage/query params */
  public markExecBodyHydrated(): void {
    this._execBodyHydrated$.next();
  }

  /** Called by table components after hydrating shared body from localStorage/query params */
  public markStormBodyHydrated(): void {
    this._stormBodyHydrated$.next();
  }

  /**
   * Saves user focus component state to localStorage
   */
  public saveUserFocusState(key: string, userFocusComponent: UserFocusComponent): void {
    this.localStorageService.set(key, {
      toggle: String(userFocusComponent.userFocusToggle),
      user: userFocusComponent.userFocus,
      primaryFilter: String(userFocusComponent.userFocusFilterMap.get('primary')),
      secondaryFilter: String(userFocusComponent.userFocusFilterMap.get('secondary')),
      followerFilter: String(userFocusComponent.userFocusFilterMap.get('follower')),
    });
  }

  /**
   * Loads user focus component state from localStorage
   */
  public loadUserFocusState(key: string, userFocusComponent: UserFocusComponent): void {
    const saved = this.localStorageService.get(key);
    if (saved) {
      userFocusComponent.userFocusToggle = saved['toggle'] === 'true';
      userFocusComponent.userFocus = saved['user'];
      userFocusComponent.userFocusFilterMap.set('primary', saved['primaryFilter'] === 'true');
      userFocusComponent.userFocusFilterMap.set('secondary', saved['secondaryFilter'] === 'true');
      userFocusComponent.userFocusFilterMap.set('follower', saved['followerFilter'] === 'true');
    }
  }

  /**
   * Loads a saved request body from localStorage into the target, applying defaults for missing fields.
   * Only applies known keys from the target to prevent leaking stale/unknown keys from old storage.
   * For sort/direction, treats empty string as missing (falls back to default).
   */
  public loadRequestBodyFromStorage(
    storageKey: string,
    target: Record<string, any>,
    defaults: Record<string, any>,
    pagination: Pagination,
  ): boolean {
    const saved = this.localStorageService.get(storageKey);
    if (!saved) {
      return false;
    }
    for (const key of Object.keys(target)) {
      if (key in saved) {
        const value = saved[key];
        // For sort/direction, treat empty string as missing so we fall back to a valid default
        if ((key === 'sort' || key === 'direction') && !value) {
          target[key] = defaults[key];
        } else {
          target[key] = value !== undefined ? value : defaults[key];
        }
      }
    }
    if (saved.page) {
      pagination.page = +saved.page;
    }
    if (saved.pagesize) {
      pagination.pagesize = +saved.pagesize;
    }
    return true;
  }

  /**
   * Saves request body + pagination to localStorage
   */
  public saveRequestBodyToStorage(
    storageKey: string,
    requestBody: Record<string, any>,
    pagination: Pagination,
  ): void {
    this.localStorageService.set(storageKey, {
      ...requestBody,
      page: pagination.page,
      pagesize: pagination.pagesize,
    });
  }

  /**
   * Detects filters field changes and notify subscribers
   */
  public filterChanged(value: any, filter: string, flag?: boolean, fieldName?: string): void {
    this.filterChanged$.next({value, filter, flag, fieldName});
  }

  /**
   * Detects filters field changes and notify subscribers
   */
  public privateFilterChanged(value: any, filter: string, flag?: boolean, fieldName?: string): void {
    this.privateFilterChanged$.next({value, filter, flag, fieldName});
  }
}
